import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Vendor } from '../../types/vendors';
import { updateVendor } from '../../features/vendors/vendorSlice';
import { useFormValidation } from '../../hooks/useFormValidation';
import Card from '../ui/Card';
import Button from '../ui/Button';
import VendorFormFields from './VendorFormFields';

interface Props {
  vendor: Vendor;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditVendorForm: React.FC<Props> = ({ vendor, onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { values, errors, handleChange, handleSubmit, isValid } = useFormValidation(
    {
      name: vendor.name,
      contact_person: vendor.contact_person || '',
      phone: vendor.phone || '',
      email: vendor.email || '',
      address: vendor.address || '',
    },
    {
      name: (value) => (!value ? t('vendors.validation.nameRequired') : ''),
      contact_person: (value) => (!value ? t('vendors.validation.contactPersonRequired') : ''),
      phone: (value) => {
        if (!value) return '';
        if (!/^[0-9+\- ]{10,15}$/.test(value)) {
          return t('vendors.validation.phoneInvalid');
        }
        return '';
      },
      email: (value) => {
        if (value && !/\S+@\S+\.\S+/.test(value)) {
          return t('vendors.validation.emailInvalid');
        }
        return '';
      },
    },
    async (values) => {
      await dispatch(updateVendor({ id: vendor.id, ...values }));
      onSuccess();
    }
  );

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-4">
        <VendorFormFields
          values={values}
          errors={errors}
          onChange={handleChange}
        />

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!isValid}
          >
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default EditVendorForm;